import { useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Brush, Syringe, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const ROLE_LABELS = {
  admin: "Admin",
  medico: "Médico",
  enfermeira_prescritora: "Enf. Tricologia",
  enfermagem: "Enfermagem",
};

function currency(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
}

function showAlertToast(data, navigate) {
  const isPackage = data.kind === "package";
  const Icon = isPackage ? Brush : Syringe;
  const label = isPackage ? "Procedimento Capilar" : "Tratamento Injetável";
  const items = (data.items || []).map((i) => `${i.name} (${i.qty}x)`).join(", ");
  const creatorLabel = ROLE_LABELS[data.created_by_role] || data.created_by_role;

  toast.custom(
    (id) => (
      <div
        data-testid="treatment-alert-toast"
        className="w-[420px] rounded-xl border border-[#E6E1DC] bg-white p-4 shadow-[0_8px_30px_rgba(11,34,48,0.12)]"
      >
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${isPackage ? "bg-[#FDF4E8] text-[#8A5A2B]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>
            <Icon className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${isPackage ? "bg-[#FDF4E8] text-[#8A5A2B]" : "bg-[#EFF6FF] text-[#1D4ED8]"}`}>
                {label}
              </span>
              <span className="text-[10px] text-[#8C98A0]">novo</span>
            </div>
            <div className="mt-1.5 text-sm font-semibold text-[#0B2230]">{data.patient_name}</div>
            <div className="mt-0.5 text-xs text-[#5C6B73]">{items}</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="font-mono text-sm font-bold text-[#0B2230]">{currency(data.total_price)}</span>
              <span className="text-[10px] text-[#8C98A0]">por {data.created_by} ({creatorLabel})</span>
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                data-testid="treatment-alert-go-btn"
                onClick={() => {
                  toast.dismiss(id);
                  if (isPackage) {
                    navigate(`/pacientes/${data.patient_id}?tab=procedures`);
                  } else {
                    navigate(`/pacientes/${data.patient_id}?tab=treatments`);
                  }
                }}
                className="flex items-center gap-1.5 rounded-lg bg-[#0B2230] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#1E445B]"
              >
                <ExternalLink className="h-3 w-3" /> Ver tratamento
              </button>
              <button
                data-testid="treatment-alert-dismiss-btn"
                onClick={() => toast.dismiss(id)}
                className="rounded-lg border border-[#E6E1DC] px-3 py-1.5 text-xs font-medium text-[#5C6B73] transition-colors hover:bg-[#F7F5F2]"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      </div>
    ),
    { duration: Infinity, position: "top-right" },
  );
}

export function TreatmentAlertListener() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hasShownPending = useRef(false);

  // Show persistent popups for unread alerts on login (once per session)
  useEffect(() => {
    if (!user || user.role !== "secretaria" || hasShownPending.current) return;
    hasShownPending.current = true;

    (async () => {
      try {
        const { data: alerts } = await api.get("/treatment-alerts");
        const unread = alerts.filter((a) => !a.read);
        // Show up to 3 most recent unread alerts as toasts
        unread.slice(0, 3).forEach((a) => {
          showAlertToast(a, navigate);
        });
        if (unread.length > 3) {
          toast.info(`Mais ${unread.length - 3} alerta(s) de tratamento pendente(s). Clique no sininho para ver todos.`, {
            duration: 8000,
            position: "top-right",
          });
        }
      } catch (_) {}
    })();
  }, [user, navigate]);

  // Handle real-time WebSocket alerts
  useEffect(() => {
    if (!user || user.role !== "secretaria") return;

    function handleAlert(event) {
      const data = event.detail;
      if (!data) return;
      showAlertToast(data, navigate);
    }

    window.addEventListener("kloss-treatment-alert", handleAlert);
    return () => window.removeEventListener("kloss-treatment-alert", handleAlert);
  }, [user, navigate]);

  return null;
}
